import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Wallet, Plus, Edit2, Trash2, TrendingUp, TrendingDown,
  CreditCard, Receipt, Repeat, ShoppingBag, AlertTriangle, CheckCircle2,
  CircleDot, History,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { RowActionsMenu } from '../../components/ui/RowActionsMenu'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { performRefresh } from '../../lib/refresh'
import {
  useBalance, useIncomeSources, useFixedExpenses, useInstallments,
  useVariableExpenses, useFinanceSummary, useSpecialObligations,
  useFinancialTargets,
} from '../../hooks/useFinance'
import { useConfirmedFinanceEvents } from '../../hooks/usePendingFinanceEvents'
import { computeCashflowForecast } from '../../lib/cashflowForecast'
import { CashflowForecast } from './components/CashflowForecast'
import { SpecialObligationsSection } from './components/SpecialObligationsSection'
import { FinancialTargetsSection } from './components/FinancialTargetsSection'
import { RiskAlertsBanner, Upcoming30Days } from './components/FinanceAlerts'
import DuesBanner from './components/DuesBanner'
import SubscriptionAuditCard from './components/SubscriptionAuditCard'
import { BalanceHistoryChart } from './components/BalanceHistoryChart'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { cn } from '../../lib/utils'

// PRD §11 says SAR is the only currency for v1, so format inline
// rather than threading a context through every leaf. Decimals only
// when needed — most amounts in the seed are whole numbers.
function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  const v = Number(n)
  const hasDecimal = Math.abs(v - Math.round(v)) > 0.005
  return v.toLocaleString('en-US', {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  })
}

const FIXED_CATEGORIES = ['family', 'transportation', 'utilities', 'subscriptions', 'other']

// =========================================================================
// Dashboard card
// =========================================================================
function DashboardCard({ summary, latest, onEditBalance, isEmpty }) {
  const { t } = useTranslation()
  const riskClasses = {
    safe:     'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30',
    warning:  'bg-amber-500/10 text-amber-600 ring-amber-500/30',
    critical: 'bg-red-500/10 text-red-600 ring-red-500/30',
  }
  const RiskIcon = summary.risk === 'safe'
    ? CheckCircle2
    : summary.risk === 'warning' ? CircleDot : AlertTriangle

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground" dir="auto">
              {t('finance.dashboard.currentBalance')}
            </p>
            <p className="text-3xl font-bold text-foreground mt-1 font-mono" dir="ltr">
              {fmt(summary.currentBalance)}{' '}
              <span className="text-sm text-muted-foreground font-normal">SAR</span>
            </p>
            {latest?.as_of_date && (
              <p className="text-[11px] text-muted-foreground mt-1" dir="auto">
                {t('finance.dashboard.asOf', { date: latest.as_of_date })}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={onEditBalance}>
            <Edit2 className="w-3.5 h-3.5 me-1" />
            {t('finance.dashboard.editBalance')}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <SummaryStat
            icon={TrendingUp}
            label={t('finance.dashboard.monthlyIncome')}
            value={summary.monthlyIncome}
            tone="positive"
          />
          <SummaryStat
            icon={TrendingDown}
            label={t('finance.dashboard.monthlyOutflow')}
            value={summary.monthlyOutflow}
            tone="negative"
          />
          <SummaryStat
            icon={Receipt}
            label={t('finance.dashboard.monthlyInstallments')}
            value={summary.monthlyInstallments}
            tone="neutral"
          />
          <SummaryStat
            icon={Wallet}
            label={t('finance.dashboard.monthlyNet')}
            value={summary.monthlyNet}
            tone={summary.monthlyNet >= 0 ? 'positive' : 'negative'}
          />
        </div>

        {/* Dashboard-level risk pill removed — RiskAlertsBanner at
            the top of the finance page already surfaces the
            forecast-derived danger card with the same expected-end
            number. Showing both meant the same warning appeared
            twice on a single screen, which felt noisy. */}
      </CardContent>
    </Card>
  )
}

function SummaryStat({ icon: Icon, label, value, tone }) {
  const toneClasses = {
    positive: 'text-emerald-600',
    negative: 'text-red-600',
    neutral:  'text-muted-foreground',
  }
  return (
    <div className="p-2 rounded-md border border-border/60 bg-muted/30">
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground" dir="auto">
        <Icon className="w-3 h-3" />
        <span className="truncate">{label}</span>
      </div>
      <p className={cn('text-sm font-semibold mt-0.5 font-mono', toneClasses[tone])} dir="ltr">
        {fmt(value)}
      </p>
    </div>
  )
}

// =========================================================================
// Balance edit form (modal-ish — inline for consistency with other sections)
// =========================================================================
function BalanceForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState(initial?.amount?.toString() || '')
  const [asOfDate, setAsOfDate] = useState(initial?.as_of_date || new Date().toLocaleDateString('en-CA'))
  const [notes, setNotes] = useState(initial?.notes || '')

  const submit = (e) => {
    e.preventDefault()
    if (!amount.trim()) return
    onSave({ amount: Number(amount), as_of_date: asOfDate, notes: notes.trim() || null })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">
          {t('finance.balance.amount')}
        </label>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          dir="ltr"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
        />
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">
          {t('finance.balance.date')}
        </label>
        <input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          dir="ltr"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
        />
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">
          {t('finance.balance.notes')}
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('finance.balance.notesPlaceholder')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !amount.trim()} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

// =========================================================================
// Income / Fixed expense / Installment forms
// =========================================================================
function IncomeForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name || '')
  const [amount, setAmount] = useState(initial?.amount?.toString() || '')
  const [frequency, setFrequency] = useState(initial?.frequency || 'monthly')
  const [receiveDay, setReceiveDay] = useState(initial?.receive_day?.toString() || '')

  const submit = (e) => {
    e.preventDefault()
    onSave({
      name: name.trim(),
      amount: Number(amount),
      frequency,
      receive_day: receiveDay ? Number(receiveDay) : null,
      available_from_day: receiveDay ? Number(receiveDay) : null,
      status: 'active',
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <input type="text" required maxLength={60}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder={t('finance.income.name')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" step="0.01" required dir="ltr"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder={t('finance.income.amount')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        <select value={frequency} onChange={(e) => setFrequency(e.target.value)} dir="auto"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
          <option value="monthly">{t('finance.income.frequencies.monthly')}</option>
          <option value="yearly">{t('finance.income.frequencies.yearly')}</option>
          <option value="one_time">{t('finance.income.frequencies.one_time')}</option>
        </select>
      </div>
      <input type="number" min={1} max={31} dir="ltr"
        value={receiveDay} onChange={(e) => setReceiveDay(e.target.value)}
        placeholder={t('finance.income.receiveDay')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !name.trim() || !amount} className="flex-1">{t('common.save')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

function FixedExpenseForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name || '')
  const [amount, setAmount] = useState(initial?.amount?.toString() || '')
  const [category, setCategory] = useState(initial?.category || 'other')
  const [chargeDay, setChargeDay] = useState(initial?.charge_day?.toString() || '1')

  const submit = (e) => {
    e.preventDefault()
    const day = Math.min(31, Math.max(1, parseInt(chargeDay, 10) || 1))
    onSave({ name: name.trim(), amount: Number(amount), category, charge_day: day, status: 'active' })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <input type="text" required maxLength={60}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder={t('finance.fixed.name')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" step="0.01" required dir="ltr"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder={t('finance.fixed.amount')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} dir="auto"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
          {FIXED_CATEGORIES.map((c) => (
            <option key={c} value={c}>{t(`finance.fixed.categories.${c}`)}</option>
          ))}
        </select>
      </div>
      <label className="block">
        <span className="text-[11px] text-muted-foreground" dir="auto">{t('finance.fixed.chargeDay')}</span>
        <input type="number" inputMode="numeric" min="1" max="31" required dir="ltr"
          value={chargeDay} onChange={(e) => setChargeDay(e.target.value)}
          className="mt-1 h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      </label>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !name.trim() || !amount} className="flex-1">{t('common.save')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

function InstallmentForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [provider, setProvider] = useState(initial?.provider || '')
  const [label, setLabel] = useState(initial?.label || '')
  const [monthly, setMonthly] = useState(initial?.monthly_amount?.toString() || '')
  const [remaining, setRemaining] = useState(initial?.remaining_months?.toString() || '')
  const [total, setTotal] = useState(initial?.total_months?.toString() || '')
  const [deductionDay, setDeductionDay] = useState(initial?.deduction_day?.toString() || '26')

  const submit = (e) => {
    e.preventDefault()
    const remainingN = Number(remaining)
    const totalN = Number(total) || remainingN
    // Compute first/last deduction months from the current month so
    // the cashflow forecast knows when this installment fires. The
    // forecast keys off last_deduction_month inclusive; without
    // these set, a freshly-added installment is invisible in the
    // projection. Convention: remaining_months counts deductions
    // starting from the current month (this month's deduction is
    // still "remaining" until 26 fires).
    const now = new Date()
    const curIdx = now.getFullYear() * 12 + now.getMonth()
    const initialFirstIdx = initial?.first_deduction_month
      ? (() => {
          const [y, m] = initial.first_deduction_month.split('-').map(Number)
          return y * 12 + (m - 1)
        })()
      : curIdx
    const firstIdx = initialFirstIdx
    const lastIdx = curIdx + Math.max(0, remainingN - 1)
    const monthKey = (idx) => {
      const y = Math.floor(idx / 12)
      const m = (idx % 12) + 1
      return `${y}-${String(m).padStart(2, '0')}`
    }
    onSave({
      provider: provider.trim(),
      label: label.trim() || null,
      monthly_amount: Number(monthly),
      remaining_months: remainingN,
      total_months: totalN,
      first_deduction_month: monthKey(firstIdx),
      last_deduction_month: remainingN > 0 ? monthKey(lastIdx) : null,
      deduction_day: Number(deductionDay) || 26,
      status: remainingN > 0 ? 'active' : 'completed',
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div className="grid grid-cols-2 gap-2">
        <input type="text" required maxLength={40}
          value={provider} onChange={(e) => setProvider(e.target.value)}
          placeholder={t('finance.installments.provider')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
        <input type="text" maxLength={40}
          value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder={t('finance.installments.label')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      </div>
      <input type="number" step="0.01" required dir="ltr"
        value={monthly} onChange={(e) => setMonthly(e.target.value)}
        placeholder={t('finance.installments.monthlyAmount')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      <div className="grid grid-cols-3 gap-2">
        <input type="number" min={0} required dir="ltr"
          value={remaining} onChange={(e) => setRemaining(e.target.value)}
          placeholder={t('finance.installments.remaining')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        <input type="number" min={1} dir="ltr"
          value={total} onChange={(e) => setTotal(e.target.value)}
          placeholder={t('finance.installments.total')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        <input type="number" min={1} max={31} dir="ltr"
          value={deductionDay} onChange={(e) => setDeductionDay(e.target.value)}
          placeholder={t('finance.installments.day')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !provider.trim() || !monthly} className="flex-1">{t('common.save')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

// =========================================================================
// Variable expenses (single-row inline editor)
// =========================================================================
function VariableExpensesCard({ row, onSave, loading }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [estimate, setEstimate] = useState('')
  const [notes, setNotes] = useState('')

  const startEdit = () => {
    setEstimate(row?.monthly_estimate?.toString() || '')
    setNotes(row?.notes || '')
    setEditing(true)
  }

  const submit = async (e) => {
    e.preventDefault()
    await onSave({ monthly_estimate: Number(estimate) || 0, notes })
    setEditing(false)
  }

  if (loading) return <Skeleton className="h-20 w-full" />

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        {editing ? (
          <form onSubmit={submit} className="space-y-2">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-app-primary-500" />
              <span className="text-sm font-semibold" dir="auto">{t('finance.variable.title')}</span>
            </div>
            <input type="number" step="0.01" required dir="ltr"
              value={estimate} onChange={(e) => setEstimate(e.target.value)}
              placeholder={t('finance.variable.amount')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
            <input type="text" maxLength={120}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t('finance.variable.notes')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">{t('common.save')}</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
            </div>
          </form>
        ) : (
          <div className="flex items-start gap-2">
            <ShoppingBag className="w-4 h-4 text-app-primary-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" dir="auto">{t('finance.variable.title')}</p>
              <p className="text-2xl font-mono mt-1" dir="ltr">
                {fmt(row?.monthly_estimate)} <span className="text-xs text-muted-foreground">SAR</span>
              </p>
              {row?.notes && (
                <p className="text-xs text-muted-foreground mt-1" dir="auto">{row.notes}</p>
              )}
            </div>
            <button type="button" onClick={startEdit}
              className="p-1 text-muted-foreground hover:text-app-primary-500" aria-label={t('common.edit')}>
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =========================================================================
// Generic CRUD section — any list of items with a renderer + form
// =========================================================================
function ListSection({
  titleKey, addKey, emptyKey, icon: Icon,
  items, loading, onCreate, onUpdate, onDelete, FormComponent, ItemRow,
  confirmTitleKey,
}) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const confirm = useConfirm()

  const handleSave = async (values) => {
    setSubmitting(true)
    if (editing) await onUpdate(editing.id, values)
    else await onCreate(values)
    setSubmitting(false)
    setAdding(false)
    setEditing(null)
  }

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: t(confirmTitleKey),
      description: item.name || item.provider,
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(item.id)
  }

  return (
    <section className="space-y-2 pt-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
          <Icon className="w-4 h-4 text-app-primary-500" />
          {t(titleKey)}
        </h2>
        {!adding && !editing && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4 me-1" />
            {t(addKey)}
          </Button>
        )}
      </div>

      {adding && <FormComponent onSave={handleSave} onCancel={() => setAdding(false)} submitting={submitting} />}

      {loading ? (
        <Skeleton className="h-16 w-full" />
      ) : items.length === 0 && !adding ? (
        <EmptyState icon={Icon} title={t(emptyKey)} />
      ) : (
        items.map((item) => (
          <React.Fragment key={item.id}>
            {editing?.id === item.id ? (
              <FormComponent
                initial={item}
                onSave={handleSave}
                onCancel={() => setEditing(null)}
                submitting={submitting}
              />
            ) : (
              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <ItemRow item={item} />
                    </div>
                    <RowActionsMenu
                      ariaLabel={t('common.actions')}
                      actions={[
                        { icon: Edit2,  label: t('common.edit'),   onClick: () => setEditing(item) },
                        { icon: Trash2, label: t('common.delete'), onClick: () => handleDelete(item), variant: 'destructive' },
                      ]}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </React.Fragment>
        ))
      )}
    </section>
  )
}

// =========================================================================
// Per-section row renderers
// =========================================================================
function IncomeRow({ item }) {
  const { t } = useTranslation()
  return (
    <>
      <p className="text-sm font-semibold" dir="auto">{item.name}</p>
      <p className="text-xs text-muted-foreground mt-0.5 font-mono" dir="ltr">
        {fmt(item.amount)} SAR · {t(`finance.income.frequencies.${item.frequency}`)}
        {item.receive_day && ` · day ${item.receive_day}`}
      </p>
    </>
  )
}

function FixedExpenseRow({ item }) {
  const { t } = useTranslation()
  return (
    <>
      <p className="text-sm font-semibold" dir="auto">{item.name}</p>
      <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
        <span className="font-mono" dir="ltr">{fmt(item.amount)} SAR</span>
        {' · '}
        {t(`finance.fixed.categories.${item.category}`)}
        {item.charge_day ? ` · ${t('finance.fixed.chargeDayShort', { day: item.charge_day })}` : ''}
      </p>
    </>
  )
}

function InstallmentRow({ item }) {
  const { t } = useTranslation()
  const remaining = Number(item.remaining_months) || 0
  const remainingTotal = remaining * Number(item.monthly_amount)
  return (
    <>
      <p className="text-sm font-semibold" dir="auto">
        {item.provider}{item.label ? ` · ${item.label}` : ''}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5 font-mono" dir="ltr">
        {fmt(item.monthly_amount)} SAR/mo · {remaining} {t('finance.installments.monthsLeft')} ({fmt(remainingTotal)})
      </p>
      {item.last_deduction_month && (
        <p className="text-[11px] text-muted-foreground mt-0.5 font-mono" dir="ltr">
          {t('finance.installments.lastMonth')}: {item.last_deduction_month}
        </p>
      )}
    </>
  )
}

// =========================================================================
// Page
// =========================================================================
export function FinancePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = () => navigate('/')

  const balance = useBalance()
  const income = useIncomeSources()
  const fixed = useFixedExpenses()
  const installments = useInstallments()
  const variable = useVariableExpenses()
  const obligations = useSpecialObligations()
  const targets = useFinancialTargets()
  const { paidSet: paidEvents } = useConfirmedFinanceEvents()

  const summary = useFinanceSummary({
    balance: balance.latest,
    incomeSources: income.sources,
    fixedExpenses: fixed.expenses,
    installments: installments.installments,
    variableExpenses: variable.row,
    specialObligations: obligations.obligations,
  })

  // 12-month projection drives the Forecast section AND the target
  // "expected by" labels, so compute it once at the page level.
  const forecastRows = React.useMemo(
    () => computeCashflowForecast({
      currentBalance: summary.currentBalance,
      incomeSources: income.sources,
      fixedExpenses: fixed.expenses,
      variableEstimate: variable.row?.monthly_estimate,
      installments: installments.installments,
      specialObligations: obligations.obligations,
      paidEvents,
      horizonMonths: 12,
    }),
    [summary.currentBalance, income.sources, fixed.expenses, variable.row, installments.installments, obligations.obligations, paidEvents],
  )

  const [editingBalance, setEditingBalance] = useState(false)
  const [savingBalance, setSavingBalance] = useState(false)

  // "Empty state" — a fresh user with no money, no income sources,
  // no expenses, no installments, no obligations. Hides the risk
  // alerts + the cashflow forecast in that case so the page reads
  // as a calm onboarding surface, not a red-zoned "you're broke"
  // warning machine. A non-zero balance OR any list with entries
  // flips us out of empty mode.
  const isEmpty = (
    Number(summary.currentBalance) === 0
    && (income.sources?.length || 0) === 0
    && (fixed.expenses?.length || 0) === 0
    && (installments.installments?.length || 0) === 0
    && (obligations.obligations?.length || 0) === 0
    && Number(variable.row?.monthly_estimate || 0) === 0
  )

  const handleSaveBalance = async (values) => {
    setSavingBalance(true)
    await balance.addEntry(values)
    setSavingBalance(false)
    setEditingBalance(false)
  }

  const handleRefresh = useCallback(
    () => performRefresh([
      balance.refetch, income.refetch, fixed.refetch,
      installments.refetch, variable.refetch,
      obligations.refetch, targets.refetch,
    ]),
    [
      balance.refetch, income.refetch, fixed.refetch,
      installments.refetch, variable.refetch,
      obligations.refetch, targets.refetch,
    ],
  )

  // DuesSheet confirms pending events server-side (balance entry
  // appended, installments.remaining_months decremented,
  // special_obligations.next_payment_date advanced). The sheet
  // fires `phoenix:refresh-finance` after a successful confirm; we
  // listen for it here and refetch the modules whose data moved.
  React.useEffect(() => {
    const onRefresh = () => {
      balance.refetch()
      installments.refetch()
      obligations.refetch()
    }
    window.addEventListener('phoenix:refresh-finance', onRefresh)
    return () => window.removeEventListener('phoenix:refresh-finance', onRefresh)
  }, [balance, installments, obligations])

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button type="button" onClick={goBack} aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground">
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              <Wallet className="w-5 h-5 text-app-primary-500" />
              {t('finance.title')}
            </h1>
          </div>
        </div>

        {!isEmpty && <DuesBanner />}

        {!isEmpty && (
          <RiskAlertsBanner
            forecastRows={forecastRows}
            installments={installments.installments}
            obligations={obligations.obligations}
          />
        )}

        {balance.loading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <DashboardCard
            summary={summary}
            latest={balance.latest}
            onEditBalance={() => setEditingBalance(true)}
            isEmpty={isEmpty}
          />
        )}

        <Upcoming30Days
          incomeSources={income.sources}
          installments={installments.installments}
          obligations={obligations.obligations}
          paidEvents={paidEvents}
        />

        {!isEmpty && <SubscriptionAuditCard />}

        {editingBalance && (
          <BalanceForm
            onSave={handleSaveBalance}
            onCancel={() => setEditingBalance(false)}
            submitting={savingBalance}
          />
        )}

        <VariableExpensesCard row={variable.row} loading={variable.loading} onSave={variable.save} />

        <ListSection
          titleKey="finance.income.title"
          addKey="finance.income.add"
          emptyKey="finance.income.empty"
          confirmTitleKey="finance.income.confirmDelete"
          icon={TrendingUp}
          items={income.sources}
          loading={income.loading}
          onCreate={income.create}
          onUpdate={income.update}
          onDelete={income.remove}
          FormComponent={IncomeForm}
          ItemRow={IncomeRow}
        />

        <ListSection
          titleKey="finance.fixed.title"
          addKey="finance.fixed.add"
          emptyKey="finance.fixed.empty"
          confirmTitleKey="finance.fixed.confirmDelete"
          icon={Repeat}
          items={fixed.expenses}
          loading={fixed.loading}
          onCreate={fixed.create}
          onUpdate={fixed.update}
          onDelete={fixed.remove}
          FormComponent={FixedExpenseForm}
          ItemRow={FixedExpenseRow}
        />

        <ListSection
          titleKey="finance.installments.title"
          addKey="finance.installments.add"
          emptyKey="finance.installments.empty"
          confirmTitleKey="finance.installments.confirmDelete"
          icon={CreditCard}
          items={installments.installments}
          loading={installments.loading}
          onCreate={installments.create}
          onUpdate={installments.update}
          onDelete={installments.remove}
          FormComponent={InstallmentForm}
          ItemRow={InstallmentRow}
        />

        <SpecialObligationsSection data={obligations} />

        {!isEmpty && (
          <CashflowForecast
            currentBalance={summary.currentBalance}
            incomeSources={income.sources}
            fixedExpenses={fixed.expenses}
            variableEstimate={variable.row?.monthly_estimate}
            installments={installments.installments}
            specialObligations={obligations.obligations}
          />
        )}

        <FinancialTargetsSection
          data={targets}
          currentBalance={summary.currentBalance}
          forecastRows={forecastRows}
          installments={installments.installments}
          obligations={obligations.obligations}
        />

        <BalanceHistoryChart entries={balance.entries} />

        {balance.entries.length > 1 && (
          <section className="space-y-2 pt-2">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 px-1" dir="auto">
              <History className="w-4 h-4 text-app-primary-500" />
              {t('finance.balance.history')}
            </h2>
            <Card>
              <CardContent className="p-0 divide-y divide-border/60">
                {balance.entries.slice(0, 8).map((e) => (
                  <div key={e.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <p className="font-mono" dir="ltr">{fmt(e.amount)} SAR</p>
                      {e.notes && <p className="text-[11px] text-muted-foreground" dir="auto">{e.notes}</p>}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono" dir="ltr">{e.as_of_date}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </PullToRefresh>
  )
}
